import React, { useState } from 'react';
import Button from '@/components/Button';
import SupplierSelectBox from '@/components/SupplierSelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import {
  ProductCategory,
  ProductCategoryCreate,
  ProductCategoryUpdate,
} from '@/models/ProductCategory';
import Supplier from '@/modules/product/models/Supplier';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';

interface ProductCategoryFormProps {
  onSubmit: (
    productCategory: ProductCategoryCreate | ProductCategoryUpdate,
  ) => Promise<void> | void;
  productCategory?: ProductCategory;
  supplier?: Supplier | null;
  supplierId?: string;
  showSupplier?: boolean;
  showSupplierSelect?: boolean;
  selectSupplierCreatedByStoreId?: string;
}

const ProductCategoryForm: React.FC<ProductCategoryFormProps> = ({
  onSubmit,
  productCategory,
  supplier: supplierProp,
  supplierId: supplierIdProp,
  showSupplier = true,
  showSupplierSelect = false,
  selectSupplierCreatedByStoreId,
}) => {
  const [loading, setLoading] = useState(false);

  const supplier = supplierProp ? supplierProp : productCategory?.supplier;
  const defaultSupplierId = supplier ? supplier.id : supplierIdProp;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductCategoryCreate | ProductCategoryUpdate>({
    defaultValues: productCategory
      ? { ...productCategory }
      : {
          name: '',
          description: '',
          status: 'ACTIVE',
          supplierId: defaultSupplierId || '',
        },
  });

  const status = watch('status');
  const supplierId = watch('supplierId');

  const handleStatusChange = (checked: boolean) => {
    setValue('status', checked ? 'ACTIVE' : 'INACTIVE');
  };

  const handleSupplierChange = (value: Supplier | null) => {
    setValue('supplierId', value ? value.id : '');
  };

  const onSubmitForm = async (data: ProductCategoryCreate | ProductCategoryUpdate) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:gap-8">
        {showSupplierSelect && (
          <div className="col-span-2">
            <SupplierSelectBox
              value={supplierId || null}
              onSelect={handleSupplierChange}
              createdByStoreId={selectSupplierCreatedByStoreId}
            />
          </div>
        )}

        {showSupplier && (
          <div className="col-span-2">
            <label htmlFor="supplier" className="block font-medium text-gray-700">
              Supplier:
            </label>
            <input
              type="text"
              id="supplier"
              className="mt-1 p-2 rounded-md w-full read-only focus:outline-none"
              value={supplier?.name || ''}
              readOnly
            />
          </div>
        )}

        <div className="col-span-2">
          <label htmlFor="name" className="block font-medium text-gray-700">
            Name:
          </label>
          <input
            type="text"
            id="name"
            {...register('name', { required: 'Name is required' })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          {errors.name && <span className="text-red-500">{errors.name.message}</span>}
        </div>

        <div className="col-span-2">
          <label htmlFor="description" className="block font-medium text-gray-700">
            Description:
          </label>
          <textarea
            id="description"
            {...register('description')}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
        </div>

        <div className="col-span-2">
          <label htmlFor="externalCode" className="block font-medium text-gray-700">
            External Code:
          </label>
          <input
            type="text"
            id="externalCode"
            {...register('externalCode')}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
        </div>

        <div className="col-span-2">
          <ToggleSwitch
            checked={status === 'ACTIVE'}
            onChange={handleStatusChange}
            labelText="Status:"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
          loading={loading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default ProductCategoryForm;
